import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Activity,
  ChevronDown,
  ChevronRight,
  Clock,
  HelpCircle,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { fullDate } from '@/lib/datetime';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import type { AlvoStatus, MonitoramentoConfig } from '../../api/monitoramento';
import {
  useIncidentes,
  useMonitoramentoConfig,
  useMonitoramentoStatus,
  useSerieAlvo,
} from '../../hooks/useMonitoramentoQuery';
import {
  GRUPOS,
  agruparTribunaisPorRamo,
  alvoComProblema,
  classificarGrupo,
  ordenarAlvos,
  resumir,
  type GrupoId,
  type RamoJusticaId,
} from './components/alvoGrouping';
import { AutenticadorStatusDot } from './components/AutenticadorStatusDot';
import { DetalheAlvoSheet } from './components/DetalheAlvoSheet';
import { GrupoAlvosHeader } from './components/GrupoAlvos';
import { IncidenteDialog } from './components/IncidenteDialog';
import { MonitoramentoFilterBar } from './components/MonitoramentoFilterBar';
import { MonitoramentoStatusPill } from './components/MonitoramentoStatusPill';
import { SaudeGruposCards } from './components/SaudeGruposCards';
import { UptimeBars } from './components/UptimeBars';
import {
  EXPERIENCE_GROUPS,
  alvosDoGrupo,
  type ExperienceGroupId,
} from '../../lib/monitoramento/experienceGrouping';

const ALVO_PORTAL = 'pdpj.portal';
type EstadoIncidente = 'ABERTO' | 'FECHADO' | 'TODOS';

export default function MonitoramentoPage() {
  const navigate = useNavigate();
  const [incidenteSelecionado, setIncidenteSelecionado] = useState<number | null>(null);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [soProblemas, setSoProblemas] = useState(false);
  const [gruposAbertos, setGruposAbertos] = useState<Record<GrupoId, boolean>>(() => {
    const estado = {} as Record<GrupoId, boolean>;
    for (const g of GRUPOS) estado[g.id] = g.abertoPorDefault;
    return estado;
  });
  const [ramosAbertos, setRamosAbertos] = useState<Record<RamoJusticaId, boolean>>({
    estadual: false,
    federal: false,
    trabalho: false,
    eleitoral: false,
    militar: false,
    outros: false,
  });
  // Inicia colapsado: a primeira dobra precisa responder "tem incidente?".
  // O histórico é drill-in — abre ao clicar no header ou ao filtrar por grupo.
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [grupoSelecionado, setGrupoSelecionado] = useState<ExperienceGroupId | null>(null);

  // O filtro de estado dos incidentes é deep-linkável via ?estado= para que
  // alertas externos (e o IncidenteDialog) possam abrir a tela já posicionada.
  const [searchParams, setSearchParams] = useSearchParams();
  const estadoIncidente = (searchParams.get('estado') as EstadoIncidente | null) ?? 'ABERTO';

  const [alvoDetalhe, setAlvoDetalhe] = useState<AlvoStatus | null>(null);
  const [detalheAberto, setDetalheAberto] = useState(false);

  const setEstadoIncidente = (estado: EstadoIncidente) => {
    const params = new URLSearchParams(searchParams);
    if (estado === 'ABERTO') {
      params.delete('estado');
    } else {
      params.set('estado', estado);
    }
    setSearchParams(params, { replace: true });
  };

  const abrirDetalheAlvo = (alvo: AlvoStatus) => {
    setAlvoDetalhe(alvo);
    setDetalheAberto(true);
  };

  const statusQuery = useMonitoramentoStatus(30_000);
  const configQuery = useMonitoramentoConfig();
  const incidentesAtivos = useIncidentes('ABERTO', 30_000);
  const alvos = useMemo(() => statusQuery.data?.alvos ?? [], [statusQuery.data?.alvos]);

  // O autenticador vira uma bolinha de status no cabeçalho — não entra no
  // histórico (filtrado em `agruparAlvos`) nem nos KPIs (evita contar duas
  // vezes o mesmo sinal).
  const autenticador = alvos.find((a) => a.alvo === 'autenticador');
  const alvosExternos = useMemo(
    () => alvos.filter((a) => a.alvo !== 'autenticador'),
    [alvos],
  );

  const incidentesAbertos = incidentesAtivos.data?.incidentes.length ?? 0;

  const portal = alvosExternos.find((a) => a.alvo === ALVO_PORTAL);
  const slo24h = portal?.uptime_24h;

  const abrirDetalhe = (id: number) => {
    setIncidenteSelecionado(id);
    setDialogAberto(true);
  };

  const dataUpdatedAt = computarSincronizadoEm(statusQuery.data?.alvos);
  const isRefreshing = statusQuery.isFetching;

  // Aplica filtros e agrupa por seção lógica (sem o autenticador, que já
  // aparece como bolinha de status no cabeçalho).
  const alvosBase = useMemo(() => {
    if (!grupoSelecionado || grupoSelecionado === 'pipeline_interna') {
      return alvosExternos;
    }
    // Filtra a lista do histórico pelos alvos do grupo selecionado — drill-in
    // nível 1 in-place (sem rota separada). O usuário vê o card destacado e
    // a lista abaixo já filtrada.
    return alvosDoGrupo(grupoSelecionado, alvosExternos);
  }, [alvosExternos, grupoSelecionado]);

  const agrupado = useMemo(
    () => agruparAlvos(alvosBase, { busca, soProblemas }),
    [alvosBase, busca, soProblemas],
  );
  const totalExibido = agrupado.pdpj.length + agrupado.tribunais.length;

  const handleSelecionarGrupo = (grupo: ExperienceGroupId | null) => {
    if (grupo === 'pipeline_interna') {
      // Card "Pipeline interna" abre a tela própria de Jobs — drill-out
      // explícito porque jobs do autenticador são uma fila operacional
      // independente do snapshot de saúde dos alvos externos.
      navigate('/app/admin/jobs');
      return;
    }
    setGrupoSelecionado(grupo);
    if (grupo) {
      // Garante que o histórico esteja aberto para o usuário ver o filtro aplicado.
      setHistoricoAberto(true);
    }
  };

  const toggleGrupo = (id: GrupoId) =>
    setGruposAbertos((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleRamo = (id: RamoJusticaId) =>
    setRamosAbertos((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ShieldCheck className="size-6" />}
        title="Monitoramento da integração"
        subtitle="Saúde da integração com PDPJ (Portal, SSO, Data Lake) e sistemas de tribunal."
        actions={
          <>
            <AutenticadorStatusDot status={autenticador?.status} />
            <SloChip valor={slo24h} />
            <LiveUpdatedIndicator
              dataUpdatedAt={dataUpdatedAt}
              isRefreshing={isRefreshing}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => statusQuery.refetch()}
              disabled={isRefreshing}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Atualizar</span>
            </Button>
            <GlossarioButton config={configQuery.data} />
          </>
        }
      />

      {/* Saúde por experiência — 5 cards de alvos externos + 1 card de
          pipeline interna. Responde "tem algo fora?" em uma olhada. Clicar
          num card filtra o histórico abaixo (drill-in nível 1 in-place);
          o card "Pipeline interna" navega para a tela própria de Jobs. */}
      <SaudeGruposCards
        alvos={alvosExternos}
        grupoSelecionado={grupoSelecionado}
        onSelecionarGrupo={handleSelecionarGrupo}
      />

      {/* Histórico por alvo (card colapsável: header sempre visível, conteúdo
          some ao colapsar para liberar espaço para incidentes/jobs) */}
      <Card>
        <CardContent className="space-y-4 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setHistoricoAberto((v) => !v)}
              aria-expanded={historicoAberto}
              className="flex flex-1 cursor-pointer items-center gap-2 text-left"
            >
              {historicoAberto ? (
                <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              )}
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Histórico por alvo (últimos 7 dias, bucket 1h)
              </h2>
            </button>
            {historicoAberto && <LegendaBars />}
          </div>
          {grupoSelecionado && grupoSelecionado !== 'pipeline_interna' && (
            <div className="flex items-center gap-2 rounded border border-primary/40 bg-primary/5 px-3 py-2 text-xs">
              <span className="text-muted-foreground">Filtrado por grupo:</span>
              <span className="font-semibold">
                {EXPERIENCE_GROUPS.find((g) => g.id === grupoSelecionado)?.label ?? grupoSelecionado}
              </span>
              <button
                type="button"
                onClick={() => setGrupoSelecionado(null)}
                className="ml-auto cursor-pointer rounded px-2 py-0.5 text-muted-foreground hover:bg-muted"
                aria-label="Remover filtro de grupo"
              >
                ×
              </button>
            </div>
          )}
          {historicoAberto && (
            <>
              <MonitoramentoFilterBar
                busca={busca}
                onBuscaChange={setBusca}
                soProblemas={soProblemas}
                onSoProblemasChange={setSoProblemas}
                totalExibido={totalExibido}
                totalBase={alvosExternos.length}
              />
              {statusQuery.isLoading ? (
                <Spinner size={32} label="Carregando alvos" />
              ) : alvosExternos.length === 0 ? (
                <EmptyState
                  icon={<Activity className="size-8" />}
                  title="Sem alvos monitorados ainda"
                  description="O poller ainda não recebeu dados do autenticador."
                />
              ) : totalExibido === 0 ? (
                <EmptyState
                  icon={<ShieldCheck className="size-8" />}
                  title="Nenhum alvo corresponde aos filtros"
                  description="Ajuste a busca ou desligue o toggle “Só problemas”."
                />
              ) : (
                <div className="space-y-2">
                  <SecaoPdpj
                    alvos={agrupado.pdpj}
                    aberto={gruposAbertos.pdpj}
                    onToggle={() => toggleGrupo('pdpj')}
                    onAbrirDetalhe={abrirDetalheAlvo}
                  />
                  <SecaoTribunais
                    alvos={agrupado.tribunais}
                    aberto={gruposAbertos.tribunais}
                    onToggle={() => toggleGrupo('tribunais')}
                    ramosAbertos={ramosAbertos}
                    onToggleRamo={toggleRamo}
                    onAbrirDetalhe={abrirDetalheAlvo}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Incidentes — uma tabela só, com filtro de estado. Substitui as antigas
          abas "ativos" e "histórico" porque a diferença entre elas é apenas o
          valor do filtro; manter duas abas duplicava a UI sem ganhar contexto. */}
      <Card>
        <CardContent className="space-y-4 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Incidentes ({incidentesAbertos} {incidentesAbertos === 1 ? 'aberto' : 'abertos'})
            </h2>
            <select
              value={estadoIncidente}
              onChange={(e) => setEstadoIncidente(e.target.value as EstadoIncidente)}
              className="cursor-pointer rounded border bg-background px-2 py-1 text-sm"
              aria-label="Filtrar incidentes por estado"
            >
              <option value="ABERTO">Apenas abertos</option>
              <option value="FECHADO">Apenas fechados</option>
              <option value="TODOS">Todos</option>
            </select>
          </div>
          <IncidentesTabela estado={estadoIncidente} onAbrir={abrirDetalhe} />
        </CardContent>
      </Card>

      <IncidenteDialog
        incidenteId={incidenteSelecionado}
        open={dialogAberto}
        onOpenChange={setDialogAberto}
      />

      <DetalheAlvoSheet
        alvo={alvoDetalhe}
        open={detalheAberto}
        onOpenChange={(open) => {
          setDetalheAberto(open);
          if (!open) setAlvoDetalhe(null);
        }}
        onDeepLinkInterno={() => setDetalheAberto(false)}
      />
    </div>
  );
}

// --- Seções do histórico ----------------------------------------------------

function SecaoPdpj({
  alvos,
  aberto,
  onToggle,
  onAbrirDetalhe,
}: {
  alvos: AlvoStatus[];
  aberto: boolean;
  onToggle: () => void;
  onAbrirDetalhe?: (alvo: AlvoStatus) => void;
}) {
  const cfg = GRUPOS.find((g) => g.id === 'pdpj')!;
  return (
    <section>
      <GrupoAlvosHeader
        titulo={cfg.label}
        descricao={cfg.descricao}
        resumo={resumir(alvos)}
        aberto={aberto}
        onToggle={onToggle}
      />
      {aberto && (
        <div className="mt-1 space-y-2 pl-6">
          {alvos.length === 0 ? (
            <p className="py-2 text-xs text-muted-foreground">Nenhum alvo visível nesta seção.</p>
          ) : (
            alvos.map((a) => <LinhaAlvo key={a.alvo} alvo={a} onAbrirDetalhe={onAbrirDetalhe} />)
          )}
        </div>
      )}
    </section>
  );
}

function SecaoTribunais({
  alvos,
  aberto,
  onToggle,
  ramosAbertos,
  onToggleRamo,
  onAbrirDetalhe,
}: {
  alvos: AlvoStatus[];
  aberto: boolean;
  onToggle: () => void;
  ramosAbertos: Record<RamoJusticaId, boolean>;
  onToggleRamo: (id: RamoJusticaId) => void;
  onAbrirDetalhe?: (alvo: AlvoStatus) => void;
}) {
  const cfg = GRUPOS.find((g) => g.id === 'tribunais')!;
  const ramos = useMemo(() => agruparTribunaisPorRamo(alvos), [alvos]);
  return (
    <section>
      <GrupoAlvosHeader
        titulo={cfg.label}
        descricao={cfg.descricao}
        resumo={resumir(alvos)}
        aberto={aberto}
        onToggle={onToggle}
      />
      {aberto && (
        <div className="mt-1 space-y-1 pl-4">
          {ramos.length === 0 ? (
            <p className="py-2 pl-4 text-xs text-muted-foreground">
              Nenhum tribunal visível. Verifique se há tribunais habilitados para peticionamento.
            </p>
          ) : (
            ramos.map(({ ramo, alvos: alvosRamo, resumo }) => {
              const abertoRamo = ramosAbertos[ramo.id];
              return (
                <div key={ramo.id}>
                  <GrupoAlvosHeader
                    titulo={ramo.label}
                    resumo={resumo}
                    aberto={abertoRamo}
                    onToggle={() => onToggleRamo(ramo.id)}
                    indentPx={8}
                  />
                  {abertoRamo && (
                    <div className="mt-1 space-y-2 pl-10">
                      {alvosRamo.map((a) => <LinhaAlvo key={a.alvo} alvo={a} onAbrirDetalhe={onAbrirDetalhe} />)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </section>
  );
}

// --- Linha de alvo (reutilizada) -------------------------------------------

function LinhaAlvo({ alvo, onAbrirDetalhe }: { alvo: AlvoStatus; onAbrirDetalhe?: (alvo: AlvoStatus) => void }) {
  const serieQuery = useSerieAlvo(alvo.alvo, '7d');
  const handleAbrir = () => onAbrirDetalhe?.(alvo);
  return (
    <div className="flex items-center gap-4">
      {/* Wrapper clicável usa role="button" (não <button>) para que o
          AlvoHint, que internamente é um <button> Popover, não fique
          aninhado em outro <button> (HTML inválido). O clique no `?` ainda
          propagaria — o stopPropagation está dentro do AlvoHint. */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleAbrir}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleAbrir();
          }
        }}
        className="min-w-[10rem] flex-1 cursor-pointer rounded px-1 py-1 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:w-52 sm:flex-none sm:shrink-0"
        aria-label={`Abrir detalhes de ${alvo.alvo}`}
      >
        <div className="flex items-center gap-1 text-sm font-medium">
          <span className="break-words">{alvo.alvo}</span>
          <AlvoHint alvo={alvo.alvo} />
        </div>
        <MonitoramentoStatusPill status={alvo.status} className="mt-1" />
      </div>
      <div className="flex-1 overflow-x-auto">
        {serieQuery.isLoading ? (
          <Spinner size={16} label={`Carregando série de ${alvo.alvo}`} />
        ) : (
          <UptimeBars buckets={serieQuery.data?.buckets ?? []} totalBarras={168} alvo={alvo.alvo} />
        )}
      </div>
    </div>
  );
}

// --- SLO chip + tabela de incidentes ---------------------------------------

/**
 * Chip compacto para o SLO 24h do Portal exibido no header. É a única
 * informação dos antigos KPIs que não estava em nenhum outro lugar — vale o
 * pixel real estate de um chip de header, mas não de uma linha inteira.
 */
function SloChip({ valor }: { valor: number | null | undefined }) {
  const formatado = formatarPct(valor);
  // Pinta de âmbar abaixo de 99% — sinal sutil de queda sem virar alarme
  // (alarme de verdade é o card vermelho do Portal em SaudeGruposCards).
  const baixo = valor !== null && valor !== undefined && valor < 0.99;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex cursor-help items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs',
            baixo
              ? 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-400'
              : 'border-border bg-muted/40 text-muted-foreground',
          )}
        >
          <ShieldCheck className="size-3" aria-hidden="true" />
          <span>SLO 24h · {formatado}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs text-xs">
        % do tempo em que o Portal do CNJ respondeu OK nas últimas 24h. Vem de chamadas reais ao
        Portal feitas pelo autenticador (peticionamento, recibo etc.).
      </TooltipContent>
    </Tooltip>
  );
}

function IncidentesTabela({
  estado,
  onAbrir,
}: {
  estado: EstadoIncidente;
  onAbrir: (id: number) => void;
}) {
  const { data, isLoading } = useIncidentes(estado, 30_000);
  if (isLoading) {
    return <Spinner size={32} label="Carregando incidentes" />;
  }
  const incidentes = data?.incidentes ?? [];
  if (incidentes.length === 0) {
    return (
      <EmptyState
        icon={<ShieldCheck className="size-8" />}
        title="Sem incidentes"
        description={
          estado === 'ABERTO'
            ? 'Nenhum incidente ativo no momento.'
            : 'Nenhum incidente registrado neste período.'
        }
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID / Alvo</TableHead>
            <TableHead className="hidden sm:table-cell">Severidade</TableHead>
            <TableHead className="hidden md:table-cell">Aberto em</TableHead>
            <TableHead className="hidden md:table-cell">Fechado em</TableHead>
            <TableHead>Ack</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {incidentes.map((i) => (
            <TableRow
              key={i.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onAbrir(i.id)}
            >
              <TableCell>
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <span>#{i.id}</span>
                    <span className="font-mono text-xs">{i.alvo}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground sm:hidden">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${severidadeClasses(i.severidade)}`}
                    >
                      {i.severidade}
                    </span>
                    <span>{fullDate(i.aberto_em)}</span>
                    {i.fechado_em && (
                      <span>· fechado {fullDate(i.fechado_em)}</span>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${severidadeClasses(i.severidade)}`}
                >
                  {i.severidade}
                </span>
              </TableCell>
              <TableCell className="hidden md:table-cell">{fullDate(i.aberto_em)}</TableCell>
              <TableCell className="hidden md:table-cell">
                {i.fechado_em ? fullDate(i.fechado_em) : '—'}
              </TableCell>
              <TableCell>{i.ack_por ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function severidadeClasses(sev: 'MENOR' | 'MAIOR' | 'CRITICO'): string {
  switch (sev) {
    case 'MENOR':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'MAIOR':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    case 'CRITICO':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    default:
      return '';
  }
}

function formatarPct(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—';
  return `${(valor * 100).toFixed(2)}%`;
}

/**
 * Timestamp (ms) mais recente de `sincronizado_em` entre os alvos do snapshot.
 * O alvo `autenticador` é incluído normalmente — desde a unificação (alvo
 * único para self + dead-man) ele reflete a atualização real do poller.
 */
function computarSincronizadoEm(alvos: { sincronizado_em: string }[] | undefined): number {
  if (!alvos || alvos.length === 0) return 0;
  let maxMs = 0;
  for (const a of alvos) {
    const ms = new Date(a.sincronizado_em).getTime();
    if (Number.isFinite(ms) && ms > maxMs) {
      maxMs = ms;
    }
  }
  return maxMs;
}

// --- Filtro/agrupamento -----------------------------------------------------

interface AgrupamentoResult {
  pdpj: AlvoStatus[];
  tribunais: AlvoStatus[];
}

function agruparAlvos(
  alvos: AlvoStatus[],
  opts: { busca: string; soProblemas: boolean },
): AgrupamentoResult {
  const termo = opts.busca.trim().toLowerCase();
  const filtrados = alvos.filter((a) => {
    if (termo && !a.alvo.toLowerCase().includes(termo)) return false;
    if (opts.soProblemas && !alvoComProblema(a)) return false;
    return true;
  });
  const pdpj: AlvoStatus[] = [];
  const tribunais: AlvoStatus[] = [];
  for (const a of filtrados) {
    const g = classificarGrupo(a.alvo);
    if (g === 'pdpj') pdpj.push(a);
    else tribunais.push(a);
  }
  return {
    pdpj: ordenarAlvos(pdpj),
    tribunais, // a ordenação de tribunais acontece dentro de cada ramo em agruparTribunaisPorRamo
  };
}

// --- Legenda e glossário ---------------------------------------------------

function LegendaBars() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
      <LegendaItem cor="bg-green-500" label="ATIVO" />
      <LegendaItem cor="bg-yellow-500" label="DEGRADADO" />
      <LegendaItem cor="bg-red-500" label="INDISPONIVEL" />
      <LegendaItem cor="bg-gray-200 dark:bg-gray-700" label="sem dados" />
    </div>
  );
}

function LegendaItem({ cor, label }: { cor: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block h-3 w-3 rounded-sm ${cor}`} />
      <span>{label}</span>
    </div>
  );
}

const DESCRICAO_ALVO: Record<string, string> = {
  autenticador:
    'Microserviço do ecossistema jurisapp que executa login, consulta processual e peticionamento. O status considera tanto a saúde interna (DB + Redis locais) quanto a conectividade entre o jurisapp e este serviço.',
  'pdpj.portal': 'Portal de Serviços do CNJ (API REST usada para consulta processual e peticionamento).',
  'pdpj.sso': 'Keycloak do CNJ (autenticação SSO que emite os tokens usados nas chamadas ao Portal).',
  'pdpj.datalake': 'Data Lake da PDPJ (cargas em lote de processos e documentos).',
};

function descricaoAlvo(alvo: string): string {
  if (DESCRICAO_ALVO[alvo]) return DESCRICAO_ALVO[alvo];
  if (alvo.startsWith('tribunal.')) {
    return 'Sistema eletrônico do tribunal (usado para peticionamento via Playwright quando o Portal não aceita a via API).';
  }
  return 'Alvo monitorado da integração.';
}

function AlvoHint({ alvo }: { alvo: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Detalhes do alvo ${alvo}`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className="inline-flex size-4 cursor-pointer items-center justify-center rounded text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <HelpCircle className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" className="w-72 text-xs">
        <div className="space-y-1">
          <div className="font-mono font-semibold">{alvo}</div>
          <p className="text-muted-foreground">{descricaoAlvo(alvo)}</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Mostra "Atualizado há alguns segundos / há 2 minutos / ..." sempre visível
// no header — indicador contínuo de que o poll está vivo, sem virar contador
// chamativo. Atualiza a cada 30s (mesmo intervalo do poll). Acima de 5min sem
// atualização, destaca em âmbar para sinalizar dados defasados.
const STALE_THRESHOLD_MS = 5 * 60 * 1000;
const REFRESH_INTERVAL_MS = 30_000;

function LiveUpdatedIndicator({
  dataUpdatedAt,
  isRefreshing,
}: {
  dataUpdatedAt: number;
  isRefreshing: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  if (!dataUpdatedAt || dataUpdatedAt <= 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Clock size={12} aria-hidden="true" />
        <span>Aguardando dados…</span>
      </span>
    );
  }

  const ageMs = Math.max(0, now - dataUpdatedAt);
  const stale = ageMs >= STALE_THRESHOLD_MS;
  const label = isRefreshing
    ? 'Atualizando…'
    : `Atualizado ${formatDistanceToNow(new Date(dataUpdatedAt), {
        locale: ptBR,
        addSuffix: true,
        includeSeconds: true,
      })}`;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs',
        stale ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground',
      )}
      title={`Última atualização: ${fullDate(dataUpdatedAt)}`}
    >
      <Clock size={12} aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

function GlossarioButton({ config }: { config: MonitoramentoConfig | undefined }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Glossário (o que significa?)"
          title="Glossário"
          className="text-muted-foreground hover:text-foreground"
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[400px] max-w-[calc(100vw-2rem)] text-xs">
        <div className="space-y-3">
          <section>
            <h3 className="text-sm font-semibold">Alvos monitorados</h3>
            <ul className="mt-1 space-y-1 text-muted-foreground">
              <li>
                <span className="font-mono">autenticador</span> — microserviço do ecossistema
                jurisapp que intermedeia PDPJ e tribunais. Status único que cobre saúde interna
                (DB + Redis) e conectividade com o jurisapp.
              </li>
              <li>
                <span className="font-mono">pdpj.portal</span> — Portal de Serviços do CNJ.
              </li>
              <li>
                <span className="font-mono">pdpj.sso</span> — Keycloak do CNJ.
              </li>
              <li>
                <span className="font-mono">pdpj.datalake</span> — Data Lake da PDPJ.
              </li>
              <li>
                <span className="font-mono">tribunal.&lt;sistema&gt;.&lt;sigla&gt;</span> — sistema
                eletrônico do tribunal (PJe, EPROC etc.). Tribunais são agrupados por ramo da
                justiça (Estadual, Federal, Trabalho, Eleitoral, Militar).
              </li>
            </ul>
          </section>
          <RegrasIncidenteSection config={config} />
          <section>
            <h3 className="text-sm font-semibold">SLO</h3>
            <p className="mt-1 text-muted-foreground">
              <em>Service Level Objective</em>. No KPI “SLO 24h (Portal)” representa o percentual
              de tempo em que o Portal do CNJ respondeu OK nas últimas 24h (uptime).
            </p>
          </section>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Renderiza a seção "O que conta como incidente" a partir da config viva do
// autenticador. Mantém os 3 níveis sempre visíveis (com "—" se ainda não houve
// resposta) para que a estrutura do glossário não desapareça enquanto carrega
// ou se o autenticador estiver inacessível.
function RegrasIncidenteSection({ config }: { config: MonitoramentoConfig | undefined }) {
  const sevs = config?.severidades;
  const jobs = config?.jobs;
  const fmtTaxa = (t: number | null | undefined) =>
    t === null || t === undefined ? '—' : `${Math.round(t * 100)}%`;
  const fmtNum = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n);

  return (
    <section>
      <h3 className="text-sm font-semibold">O que conta como incidente</h3>
      <p className="mt-1 text-muted-foreground">
        O detector avalia uma janela deslizante de {fmtNum(config?.janela_min)} min (re-avalia a
        cada {fmtNum(config?.intervalo_detector_s)}s) e abre um incidente quando o alvo acumula
        falhas consecutivas (apenas <span className="font-mono">INDISPONIVEL</span>) ou taxa de
        erro (inclui <span className="font-mono">DEGRADADO</span>) acima do limiar:
      </p>
      <ul className="mt-1 space-y-0.5 text-muted-foreground">
        <li>
          <span className="font-semibold text-yellow-700 dark:text-yellow-400">MENOR</span> —{' '}
          {fmtNum(sevs?.MENOR.consecutivas)} falhas consecutivas ou ≥ {fmtTaxa(sevs?.MENOR.taxa_erro)}{' '}
          de erro.
        </li>
        <li>
          <span className="font-semibold text-orange-700 dark:text-orange-400">MAIOR</span> —{' '}
          {fmtNum(sevs?.MAIOR.consecutivas)} falhas consecutivas ou ≥ {fmtTaxa(sevs?.MAIOR.taxa_erro)}{' '}
          de erro.
        </li>
        <li>
          <span className="font-semibold text-red-700 dark:text-red-400">CRITICO</span> —{' '}
          {fmtNum(sevs?.CRITICO.consecutivas)} falhas consecutivas ou ≥{' '}
          {fmtNum(sevs?.CRITICO.indisponivel_min)} min indisponível.
        </li>
      </ul>
      <p className="mt-1 text-muted-foreground">
        Regras de taxa exigem ≥ {fmtNum(config?.min_amostras_para_taxa)} amostras na janela
        (CRITICO ignora esse mínimo). Incidentes fecham automaticamente após{' '}
        {fmtNum(config?.fechamento_consecutivas_ativo)} amostras consecutivas ATIVO.
      </p>
      <p className="mt-1 text-muted-foreground">
        <span className="font-semibold">Pipeline interna (jobs):</span> detector separado que abre
        incidente em alvos <span className="font-mono">autenticador.&lt;fila&gt;</span> com
        severidade fixa <span className="font-mono">{jobs?.severidade_fixa ?? '—'}</span> quando a
        taxa de falha em {fmtNum(jobs?.janela_min)} min ≥ {fmtTaxa(jobs?.taxa_falha_threshold)} e
        há ao menos {fmtNum(jobs?.min_falhas)} falhas absolutas.
      </p>
      {!config && (
        <p className="mt-2 text-[11px] italic text-muted-foreground">
          Limiares carregando do autenticador…
        </p>
      )}
    </section>
  );
}
